import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ResultBadge, inr } from "@/components/engage/ResultBadge";
import { PreFlightPanel } from "@/components/engage/PreFlightPanel";
import { RuleAuditPanel } from "@/components/engage/RuleAuditPanel";
import { PlanPanel } from "@/components/engage/PlanPanel";
import { POControlsPanel } from "@/components/engage/POControlsPanel";
import { CreatorsPanel, DeliverablesPoEPanel } from "@/components/engage/CreatorsPanel";
import { AutoFetchPanel } from "@/components/engage/AutoFetchPanel";
import { AIReviewSheet } from "@/components/engage/AIReviewSheet";
import { InvoiceIntelligencePanel } from "@/components/engage/InvoiceIntelligencePanel";
import { ScenarioSwitcher, ReconciliationPanel } from "@/components/engage/ScenarioSwitcher";
import { ExceptionList } from "@/components/engage/ExceptionList";
import { ClearancePanel, PaymentPanel } from "@/components/engage/ClearancePayment";
import { Download } from "lucide-react";

const HOME = { ey_auditor: "/ey", procurement_desk: "/procurement", customer: "/customer", propay_admin: "/admin", influencer: "/influencer", hul_approver: "/approver" };
const TABS = ["overview", "preflight", "plan", "po", "creators", "deliverables", "audit", "invoices", "reconciliation", "exceptions", "clearance", "payment"];

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [v, setV] = useState(null);
  const [aiDel, setAiDel] = useState(null);
  const tab = TABS.includes(sp.get("tab")) ? sp.get("tab") : "overview";
  const home = HOME[user?.role] || "/admin";
  const canEdit = ["customer", "procurement_desk", "propay_admin"].includes(user?.role);
  const canOverride = ["customer", "propay_admin"].includes(user?.role);

  const load = useCallback(() => api.get(`/campaigns/${id}/engage`).then((r) => setV(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Load failed")), [id]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    const resp = await fetch(`${BACKEND_URL}/api/campaigns/${id}/ey-audit-trail.pdf`, { credentials: "include" });
    if (!resp.ok) return toast.error("PDF failed");
    const url = URL.createObjectURL(await resp.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: `audit-${v.campaign.campaign_number}.pdf` });
    a.click(); URL.revokeObjectURL(url);
  };

  if (!v) return <div className="p-8 text-slate-400 text-sm">Loading campaign…</div>;
  const c = v.campaign;
  return (
    <div className="space-y-6" data-testid="campaign-detail">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500"><Link className="hover:underline" to={`${home}/campaigns`}>← Campaigns</Link><ScenarioSwitcher current={id} /></div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl sm:text-3xl font-black text-slate-900" data-testid="campaign-name">{c.name}</h1>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400">Pre-flight <ResultBadge value={c.preflight_status || "NOT_APPLICABLE"} data-testid="hdr-preflight" /></span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400">Audit <ResultBadge value={c.audit_status || "NOT_APPLICABLE"} data-testid="hdr-audit" /></span>
            </div>
            <div className="text-sm text-slate-500 mt-1">{c.campaign_number} · {c.brand || "—"} · {c.product} · {c.tenant_name} · {c.start_date} → {c.end_date} · Agency: {c.agency || "—"}</div>
            {c.demo_scenario && <div className="mt-1 text-[11px] font-mono text-amber-700 bg-amber-50 border border-amber-200 inline-block px-2 py-0.5 rounded" data-testid="demo-scenario">demo scenario · {c.demo_scenario.replace(/_/g, " ")}</div>}
          </div>
          <Button variant="outline" onClick={downloadPdf} className="border-[#0052FF] text-[#0052FF]" data-testid="download-ey-audit-pdf-btn"><Download size={16} className="mr-1" /> Audit-trail PDF</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
          {[["Approved value", inr(v.financials.approved)], ["PO balance", v.po ? inr(v.po.balance) : "No PO"], ["Invoiced", inr(v.financials.invoiced)], ["Paid", inr(v.financials.paid)], ["Creators", `${v.creators.length}`]].map(([l, val]) => (
            <div key={l} data-testid={`kpi-${l.toLowerCase().replace(/ /g, "-")}`}><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="font-mono text-xl font-bold text-slate-900">{val}</div></div>
          ))}
        </div>
      </div>

      <Tabs value={tab} onValueChange={(t) => setSp({ tab: t })}>
        <TabsList className="flex flex-wrap h-auto justify-start bg-slate-100 p-1">
          {[["overview", "Overview"], ["preflight", "Pre-Flight"], ["plan", "Plan & Creative"], ["po", "PO Controls"], ["creators", "Creators"], ["deliverables", "Deliverables / PoE"], ["audit", "Rule Audit"], ["invoices", "Invoices"], ["reconciliation", "Reconciliation"], ["exceptions", "Exceptions"], ["clearance", "Clearance"], ["payment", "Payment"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="data-[state=active]:bg-white data-[state=active]:text-[#0052FF] data-[state=active]:shadow-sm">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-5"><Overview v={v} setTab={(t) => setSp({ tab: t })} /></TabsContent>
        <TabsContent value="preflight" className="mt-5"><PreFlightPanel cid={id} preflight={v.preflight} canOverride={canOverride} onChange={load} /></TabsContent>
        <TabsContent value="plan" className="mt-5"><PlanPanel campaign={c} planCost={v.plan_cost} canEdit={canEdit} onChange={load} /></TabsContent>
        <TabsContent value="po" className="mt-5"><POControlsPanel campaign={c} po={v.po} planCost={v.plan_cost} canEdit={canEdit} onChange={load} /></TabsContent>
        <TabsContent value="creators" className="mt-5"><CreatorsPanel campaign={c} creators={v.creators} canEdit={canEdit} home={home} onChange={load} /></TabsContent>
        <TabsContent value="deliverables" className="mt-5 space-y-5"><AutoFetchPanel cid={id} onChange={load} /><DeliverablesPoEPanel deliverables={v.deliverables} onAIReview={setAiDel} /></TabsContent>
        <TabsContent value="audit" className="mt-5"><RuleAuditPanel cid={id} onChange={load} /></TabsContent>
        <TabsContent value="invoices" className="mt-5"><InvoiceIntelligencePanel campaign={c} invoices={v.invoices} canEdit={canEdit} onChange={load} /></TabsContent>
        <TabsContent value="reconciliation" className="mt-5"><ReconciliationPanel cid={id} key={v.financials.invoiced + "-" + v.deliverables.length + "-" + (v.audit_run?.id || "")} /></TabsContent>
        <TabsContent value="exceptions" className="mt-5"><ExceptionList campaignId={id} compact /></TabsContent>
        <TabsContent value="clearance" className="mt-5"><ClearancePanel campaign={c} onChange={load} /></TabsContent>
        <TabsContent value="payment" className="mt-5"><PaymentPanel campaign={c} onChange={load} /></TabsContent>
      </Tabs>
      <AIReviewSheet deliverable={aiDel} campaign={c} canOverride={canOverride || user?.role === "ey_auditor"} onClose={() => setAiDel(null)} onChange={load} />
    </div>
  );
}

function creativeResult(status) {
  if (status === "approved") return "PASS";
  return status === "pending" ? "NEEDS_REVIEW" : "FAIL";
}

function poResult(po) {
  if (!po) return "FAIL";
  return po.valid_to < new Date().toISOString().slice(0, 10) ? "FAIL" : "PASS";
}

function Overview({ v, setTab }) {
  const c = v.campaign, plan = c.plan || {}, creative = c.creative || {};
  const steps = [
    ["Plan approved", plan.status === "approved" ? "PASS" : "FAIL", `v${plan.version || 0}`, "plan"],
    ["Creative approved", creativeResult(creative.status), creative.asset_name || "—", "plan"],
    ["PO linked & valid", poResult(v.po), v.po?.po_number || "none", "po"],
    ["Pre-flight", v.preflight ? v.preflight.status : "NOT_APPLICABLE", v.preflight ? `${v.preflight.score}%` : "not run", "preflight"],
    ["Content fetched", v.deliverables.length ? "PASS" : "NEEDS_REVIEW", `${v.deliverables.length} items`, "deliverables"],
    ["Rule audit", v.audit_run ? v.audit_run.status : "NOT_APPLICABLE", v.audit_run ? `${v.audit_run.counts.FAIL} fail · ${v.audit_run.counts.NEEDS_REVIEW} review` : "not run", "audit"],
    ["Invoices", v.invoices.length ? "PASS" : "NOT_APPLICABLE", `${v.invoices.length} received`, "invoices"],
    ["Reconciliation", "NOT_APPLICABLE", "plan ↔ PO ↔ PoE ↔ invoice ↔ payment", "reconciliation"],
    ["Exceptions & clearance", c.clearance_at ? "PASS" : "NEEDS_REVIEW", c.clearance_at ? "lines cleared" : "awaiting clearance", "clearance"],
    ["Payment memo → PTP", "NOT_APPLICABLE", "KYS gate · HUL approval · PTP", "payment"],
  ];
  return (
    <div className="grid gap-5 lg:grid-cols-3" data-testid="campaign-overview">
      <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
        <div className="font-heading font-bold text-slate-900 mb-4">Lifecycle readiness</div>
        <ul className="divide-y divide-slate-100">
          {steps.map(([l, r, hint, t]) => (
            <li key={l} className="flex items-center justify-between py-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" onClick={() => setTab(t)} data-testid={`overview-step-${t}`}>
              <span className="text-sm text-slate-700">{l}</span>
              <span className="flex items-center gap-3"><span className="text-xs text-slate-500 font-mono">{hint}</span><ResultBadge value={r} /></span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm space-y-3">
        <div className="font-heading font-bold text-slate-900">Brief</div>
        <p className="text-slate-600 leading-relaxed">{c.briefing_note}</p>
        <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
          <div>Hashtag <div className="font-mono text-slate-800">{plan.required_hashtag || "—"}</div></div>
          <div>Brand handle <div className="font-mono text-slate-800">{plan.brand_handle || "—"}</div></div>
          <div>AdFluence ref <div className="font-mono text-slate-800">{c.adfluence_ref}</div></div>
          <div>Execution <div className="text-slate-800 capitalize">{c.execution_status || "—"}</div></div>
        </div>
      </div>
    </div>
  );
}
